import { Token } from './Token.js'

/**
 * This helper provides the parent capability for a website to
 * check and activate a daemon, and to create a device offer for
 * subsequent claim by consumer devices or browsers.
 *
 * The helper can operate either browser- or server-side.
 *
 * Usage
 * =====
 *
 * // Create the helper for a child daemon.
 * const helper = new ParentHelper()
 *
 * // Initialise with the child daemon being parented (aud), the
 * // source (src), the private key used to sign the token
 * // and the signatory url (iss).
 * await initWithKey(child, source, privateJwk, iss)
 *
 * // Succeeds if the child daemon is activated.
 * await helper.checkActivate()
 *
 * // Returns the claim code and check state of a new device offer.
 * await helper.fetchOffer()
 *
 * @typedef {Object} JsonWebKey
 * @typedef {string} TokenBase64
 *
 * @typedef {Object} OfferOptions
 * @property {'TRANSIENT' | 'PERMANENT'} [type] the type of the device.
 * @property {number} [ttl] the time-to-live of device (transient only), in seconds.
 * @property {number} [expiry] the number of seconds before the offer expires.
 * @property {Object} [payload] the optional offer payload.
 */

export class ParentHelper {
  /** @type {JsonWebKey | undefined} */
  #privateJwk // Signs the token used for activate and offer.

  /** @type {string | undefined} */
  #protocol   // Protocol in use (http: or https:).

  /** @type {string | undefined} */
  #child     // The host name of the daemon being parented.

  /** @type {string | undefined} */
  #iss      // Callback-supplied URL for the pubicly visible issuer object.

  /** @type {string | undefined} */
  #src      // The HTML source URL, which must match origin: header iff present in daemon request.

  /** @type {Token | undefined} */
  #token    // The token used for check activate and offer calls to the daemon.

  /** @type {string | undefined} */
  #claimCode  // The claim code returned by fetchOffer.

  /**
   * Initialises the helper with details already known to the caller.
   *
   * The private key is used to sign generated tokens, where the issUrl
   * references the public key used to verify the token.
   *
   * This method is used by tab runners wanting to parent daemons.
   *
   * @param {string} child
   * @param {string} src # Note can be party:control.
   * @param {JsonWebKey} privateJwk
   * @param {string} iss
   */
  async initWithKey(child, src, privateJwk, iss) {
    this.#protocol = src.startsWith('https:') ? 'https:' : 'http:'
    this.#child = child
    this.#src = src
    this.#privateJwk = privateJwk
    this.#iss = iss

    await this.#buildToken()
  }

  /**
   * Uses the token to check activate the daemon. An already
   * activated daemon with this parent is retained, or a new
   * daemon is created so long as the sub of the pre-generated
   * token matches a parent record in the provider.
   *
   * The `isNew` attribute in the ok result can be used to
   * determine if the daemon is newly activated or already
   * active.s
   *
   * @returns boolean true if newly activated, otherwise false.
   * @throws {string} exception if daemon cannot be activated.
   */
  async checkActivate() {
    if (!this.#token) {
      throw `checkActivate: No token`
    }
    const url = new URL(`${this.#token.getAud()}/activate`)
    const token = this.#token.asSignedBase64()
    const body = {}
    let response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'x-tabserver-token': token,
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      const {
        ok,
        error
      } = await response.json()

      if (error) {
        throw error
      }

      if (ok.isNew) {
        return true
      }

      return false
    }
    catch (e) {
      console.error(e)
      throw `Cannot activate ${this.#token.getParty()}`
    }
  }

  /**
   * Uses the token to create a device offer on the daemon with default
   * type 'TRANSIENT' and ttl of 300s.
   *
   * @typedef {Object} Offer
   * @property {string} claimCode
   * @property {string} checkState
   *
   * @param {'NO_CHECK' | 'DO_CHECK'} flow to use. For QR offers, use checked flow.
   * @param {OfferOptions | null} options to use when making the offer, if any.
   * @return {Promise<Offer>} offer claimCode and checkState ('NO_CHECK' or 'AWAIT_CHECK').
   */
  async makeOffer(flow, options = null) {
    const {
      type = 'TRANSIENT',
      ttl = 300,
      expiry = 30,
      payload
    } = options ?? {}

    if (!this.#token) {
      throw `makeOffer: No token`
    }
    const url = new URL(`${this.#token.getAud()}/offer`)
    const token = this.#token.asSignedBase64()
    const body = {
      role: 'party',
      type,
      ttl,
      description: `Offered by ${this.#token.getCounterparty()}`,
      payload,
      expiry: new Date(Date.now() + 1000 * expiry),
      flow
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-tabserver-token': token,
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    })
    const json = await response.json()
    if ('error' in json) {
      throw json.error
    }

    const {
      claimCode,
      _checkState
    } = json.ok

    this.#claimCode = claimCode

    return json.ok
  }

  /**
   * Returns the check state for the offer. This is used when
   * awaiting claim.
   *
   * @return {Promise<string>}
   */
  async checkState() {
    if (!this.#token || !this.#claimCode) {
      throw 'checkState: No token or claimCode'
    }
    const url = new URL(`${this.#token.getAud()}/offer/check`)
    url.searchParams.append('claimCode', this.#claimCode)
    const token = this.#token.asSignedBase64()
    const response = await fetch(url, {
      headers: {
        'x-tabserver-token': token
      }
    })
    const json = await response.json()
    if ('error' in json) {
      throw json.error
    }

    /**
     * Result has `claimCode` and `checkState`.
     */
    return json.ok.checkState
  }

    /**
   * Posts the check code that confirms the offering device has
   * got the expected claiming device, and returns the resulting check state.
   *
   * @typedef {Object} ConfirmResult
   * @property {string} checkState
   *
   * @param {string} checkCode the check code obtained from the claiming device.
   * @return {Promise<ConfirmResult>} the check state following confirmation.
   */
    async confirmClaim(checkCode) {
      if (!this.#token || !this.#claimCode) {
        throw 'confirmClaim: No token or claimCode'
      }

      const url = new URL(`${this.#token.getAud()}/offer/confirm`)
      const token = await this.#token.asSignedBase64()
      const body = {
        claimCode: this.#claimCode,
        checkCode
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'x-tabserver-token': token,
          'content-type': 'application/json'
        },
        body: JSON.stringify(body)
      })
      const json = await response.json()
      if ('error' in json) {
        throw json.error
      }
      return json.ok
    }

  /**
   * Returns the daemon name.
   *
   * @return {string} daemon name, e.g. 'daemon.once.id'.
   */
  getChild() {
    if (!this.#child) {
      throw 'getChild: no child'
    }
    return this.#child
  }

  /**
   * Returns the daemon origin, suitable for redirection or claiming
   * a code.
   *
   * @return {string} child daemon origin e.g. 'https://daemon.once.id'.
   */
  getChildOrigin() {
    if (!this.#iss || !this.#child) {
      throw 'getDaemonUrl: no issUrl or daemon'
    }
    return `${this.#protocol}//${this.#child}`
  }

  /**
   * Builds the token used in the check activate and offer calls toh
   * the daemon.
   *
   * @return {Promise<void>}
   */
  async #buildToken() {
    if (!this.#iss || !this.#child || !this.#privateJwk) {
      throw 'buildToken: missing iss, child or privateJwk'
    }

    const iss = this.#iss
    const aud = this.getChildOrigin()
    const sub = new URL(this.#iss).origin

    // The `src` attribute is origin, pathname and search (no fragment).
    let src
    if (this.#src) {
      const srcUrl = new URL(this.#src)
      src = `${srcUrl.origin}${srcUrl.pathname}${srcUrl.search}`
    }
    else {
      src = 'party:control'
    }

    const payload = {
      iss,
      aud,
      sub,
      src,
      scope: {
        'party:control': 'offer'
      },
      iat: Date.now(),
      exp: Date.now() + 1000 * 60 * 3
    }

    const token = new Token(payload)
    await token.signWith(Promise.resolve(this.#privateJwk))
    this.#token = token
  }
}
